import { useState, useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsBoxArrowInRight } from 'react-icons/bs';
import { HiCheckCircle, HiSearch } from 'react-icons/hi';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';
import { useMergeProspectQueries } from '#leadOverviewFeature/models/prospectQueries/mutations';

const MergeQueryModal = ({ isOpen, onClose, sourceQuery, queries, onMergeComplete }) => {
	const { showError } = useError();
	const mergeMutation = useMergeProspectQueries({
		onSuccess: (response) => {
			setShowSuccessState(true);
			setMergeResult(response);
			setTimeout(() => {
				handleClose();
				onMergeComplete?.();
			}, 2000);
		},
		onError: (error) => {
			showError({
				title: 'Fehler beim Zusammenführen',
				error,
			});
		},
	});

	const [selectedTarget, setSelectedTarget] = useState(null);
	const [search, setSearch] = useState('');
	const [showSuccessState, setShowSuccessState] = useState(false);
	const [mergeResult, setMergeResult] = useState(null);

	// Reset form when modal opens/closes
	useEffect(() => {
		if (isOpen) {
			setSelectedTarget(null);
			setSearch('');
			setShowSuccessState(false);
			setMergeResult(null);
		}
	}, [isOpen]);

	// Filter queries: exclude source query, shared queries, and apply search
	const filteredQueries = useMemo(() => {
		if (!queries || !sourceQuery) return [];

		return queries
			.filter((q) => q.id !== sourceQuery.id)
			.filter((q) => !q.isShared)
			.filter((q) => q.queryName?.toLowerCase().includes(search.toLowerCase()));
	}, [queries, sourceQuery, search]);

	const handleClose = () => {
		if (!mergeMutation.isPending) {
			onClose();
		}
	};

	const handleMerge = () => {
		if (!selectedTarget || !sourceQuery) return;

		mergeMutation.mutate({
			sourceId: sourceQuery.id,
			targetId: selectedTarget.id,
		});
	};

	const sourceResultCount = sourceQuery?.companyCount ?? 0;

	if (showSuccessState && mergeResult) {
		return (
			<Modal isOpen={isOpen} onClose={handleClose} size="s">
				<div className="flex flex-col items-center justify-center text-center py-4">
					<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
						<HiCheckCircle className="w-8 h-8 text-success-700" />
					</div>
					<h3 className="text-lg font-semibold mb-2 text-success-700">Erfolgreich zusammengeführt!</h3>
					<p className="text-sm text-text mb-2">
						{mergeResult.movedCompanies}{' '}
						{mergeResult.movedCompanies === 1 ? 'Unternehmen wurde' : 'Unternehmen wurden'} verschoben.
					</p>
					{mergeResult.skippedDuplicates > 0 && (
						<p className="text-xs text-bgBase-800">
							{mergeResult.skippedDuplicates}{' '}
							{mergeResult.skippedDuplicates === 1 ? 'Duplikat wurde' : 'Duplikate wurden'} übersprungen.
						</p>
					)}
				</div>
			</Modal>
		);
	}

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="l">
			<Panel.Header
				icon={BsBoxArrowInRight}
				onClose={handleClose}
				subtitle={`"${sourceQuery?.queryName}" mit einer anderen Liste zusammenführen`}
			>
				Listen zusammenführen
			</Panel.Header>
			<Panel.Body>
				<div className="p-3 bg-primary-100 border border-primary-200 rounded-md mb-4">
					<p className="text-sm text-primary-700">
						Alle <strong>{sourceResultCount}</strong>{' '}
						{sourceResultCount === 1 ? 'Unternehmen' : 'Unternehmen'} werden in die Ziel-Liste verschoben.
						Die Quell-Liste wird anschließend gelöscht. Bereits vorhandene Unternehmen werden übersprungen.
					</p>
				</div>

				<div className="mb-4">
					<div className="relative">
						<TextInput
							className="pr-10"
							placeholder="Ziel-Liste suchen..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<HiSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-bgBase-800 w-5 h-5" />
					</div>
				</div>

				<div className="border border-bgBase-400 rounded-lg overflow-hidden">
					<div className="max-h-80 overflow-y-auto">
						{filteredQueries.length === 0 ? (
							<div className="p-8 text-center text-bgBase-800">
								<BsBoxArrowInRight className="w-12 h-12 mx-auto mb-2 text-bgBase-800" />
								<p className="text-sm">
									{search ? 'Keine Listen gefunden' : 'Keine anderen Listen vorhanden'}
								</p>
							</div>
						) : (
							filteredQueries.map((query) => (
								<button
									key={query.id}
									type="button"
									className={`w-full px-4 py-3 text-left border-b border-bgBase-400 last:border-0 transition-colors ${
										selectedTarget?.id === query.id
											? 'bg-primary-100 border-l-4 border-l-primary-500'
											: 'hover:bg-gray-50 border-l-4 border-l-transparent'
									}`}
									onClick={() => setSelectedTarget(query)}
								>
									<div className="flex items-center justify-between">
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-text truncate">{query.queryName}</p>
											{selectedTarget?.id === query.id && (
												<p className="text-xs text-primary-700 mt-0.5">Ausgewählt</p>
											)}
										</div>
										<span className="inline-flex items-center px-2.5 py-1 text-xs font-bold rounded-full bg-gray-100 text-text border border-bgBase-400 ml-3">
											{query.companyCount ?? 0}
										</span>
									</div>
								</button>
							))
						)}
					</div>
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={handleClose} disabled={mergeMutation.isPending}>
					Abbrechen
				</Button>
				<Button variant="solid" disabled={!selectedTarget || mergeMutation.isPending} onClick={handleMerge}>
					{mergeMutation.isPending ? (
						<>
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
							Zusammenführen...
						</>
					) : (
						<>
							<BsBoxArrowInRight className="mr-2" />
							Zusammenführen
						</>
					)}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

MergeQueryModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceQuery: PropTypes.object,
	queries: PropTypes.array.isRequired,
	onMergeComplete: PropTypes.func,
};

export default MergeQueryModal;
